import { useEffect, useRef, useState, type KeyboardEvent } from 'react'

import { DataTableCard } from '../DataTableCard'
import type { PsRatingRow } from '../../services/api'
import { fmtRating, formatDateTime, signed } from '../../utils/psRatingFormat'
import {
  PS_RATING_MAX,
  PS_RATING_MIN,
  PS_RATING_STEP,
  roundRating,
} from '../../utils/psRatingZeroSum'

interface Props {
  rows: PsRatingRow[]
  draft: Record<string, number>
  activeTeam: string | null
  hasDraft: boolean
  saving: boolean
  fillViewport?: boolean
  embedded?: boolean
  onActivate: (team: string) => void
  onChange: (team: string, value: number) => void
}

export function PsRatingAdjustTable({
  rows,
  draft,
  activeTeam,
  hasDraft,
  saving,
  fillViewport = true,
  embedded = true,
  onActivate,
  onChange,
}: Props) {
  const caption = (
    <span>
      Click a row to select a team, then use{' '}
      <kbd className="rounded border border-slate-200 bg-slate-50 px-1 font-mono text-[11px] text-slate-700">
        ←
      </kbd>{' '}
      <kbd className="rounded border border-slate-200 bg-slate-50 px-1 font-mono text-[11px] text-slate-700">
        →
      </kbd>{' '}
      to fine-tune by {PS_RATING_STEP}, or type the effective rating in the box (range{' '}
      {PS_RATING_MIN} to {PS_RATING_MAX}).
    </span>
  )

  return (
    <DataTableCard embedded={embedded} fillViewport={fillViewport} caption={caption}>
      <table className="table w-full table-fixed">
        <colgroup>
          <col style={{ width: '34%' }} />
          <col style={{ width: '46%' }} />
          <col style={{ width: '20%' }} />
        </colgroup>
        <thead>
          <tr>
            <th>Team</th>
            <th>Rating</th>
            <th className="text-right">
              <span className="ml-auto inline-block w-[5.25rem]">Effective</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <TeamRatingRow
              key={row.team}
              row={row}
              value={draft[row.team] ?? row.effective_rating}
              isActive={activeTeam === row.team}
              isFrozen={hasDraft && activeTeam !== row.team}
              rowLocked={saving || (hasDraft && activeTeam !== row.team)}
              onActivate={() => onActivate(row.team)}
              onChange={(v) => onChange(row.team, v)}
            />
          ))}
        </tbody>
      </table>
    </DataTableCard>
  )
}

function TeamRatingRow({
  row,
  value,
  isActive,
  isFrozen,
  rowLocked,
  onActivate,
  onChange,
}: {
  row: PsRatingRow
  value: number
  isActive: boolean
  isFrozen: boolean
  rowLocked: boolean
  onActivate: () => void
  onChange: (value: number) => void
}) {
  const rowRef = useRef<HTMLTableRowElement>(null)
  const offset = value - row.effective_rating
  const dirty = Math.abs(offset) >= 0.0005

  const nudge = (delta: number) => {
    const next = roundRating(
      Math.min(PS_RATING_MAX, Math.max(PS_RATING_MIN, value + delta)),
    )
    onChange(next)
  }

  const onRowKeyDown = (e: KeyboardEvent<HTMLTableRowElement>) => {
    if (!isActive || rowLocked) return
    if (e.key === 'ArrowLeft') {
      e.preventDefault()
      nudge(-PS_RATING_STEP)
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      nudge(PS_RATING_STEP)
    }
  }

  const activateRow = () => {
    if (rowLocked) return
    onActivate()
    rowRef.current?.focus({ preventScroll: true })
  }

  return (
    <tr
      ref={rowRef}
      tabIndex={rowLocked ? -1 : 0}
      className={[
        'outline-none transition-colors',
        !rowLocked ? 'cursor-pointer' : '',
        isActive ? 'bg-blue-50 ring-2 ring-inset ring-blue-500/30' : '',
        isFrozen && dirty ? 'bg-slate-50/80' : '',
      ].join(' ')}
      aria-selected={isActive}
      onClick={activateRow}
      onKeyDown={onRowKeyDown}
    >
      <td className="align-middle">
        <p className="truncate font-medium text-slate-900">{row.team}</p>
        <p className="truncate text-[11px] text-slate-500">
          Baseline {fmtRating(row.baseline_rating)}
          {row.last_changed_at ? ` · ${formatDateTime(row.last_changed_at)}` : ''}
        </p>
      </td>
      <td className="align-middle">
        <SliderWithHistory
          value={value}
          history={row.history}
          baseline={row.baseline_rating}
          disabled={rowLocked}
          onActivate={activateRow}
          onChange={onChange}
        />
      </td>
      <td className="align-middle text-right" onClick={(e) => e.stopPropagation()}>
        <EffectiveRatingCell
          value={value}
          offset={offset}
          dirty={dirty}
          isActive={isActive}
          disabled={rowLocked}
          onActivate={activateRow}
          onChange={onChange}
        />
      </td>
    </tr>
  )
}

function EffectiveRatingCell({
  value,
  offset,
  dirty,
  isActive,
  disabled,
  onActivate,
  onChange,
}: {
  value: number
  offset: number
  dirty: boolean
  isActive: boolean
  disabled: boolean
  onActivate: () => void
  onChange: (value: number) => void
}) {
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState(() => fmtRating(value))

  useEffect(() => {
    if (!editing) setText(fmtRating(value))
  }, [value, editing])

  const commit = () => {
    setEditing(false)
    const n = Number(text.trim())
    if (!Number.isFinite(n)) {
      setText(fmtRating(value))
      return
    }
    onChange(roundRating(Math.min(PS_RATING_MAX, Math.max(PS_RATING_MIN, n))))
  }

  const offsetLine = (
    <p
      className={[
        'h-4 font-mono text-xs tabular-nums leading-4',
        !dirty ? 'invisible' : isActive ? 'text-blue-700' : 'text-slate-500',
      ].join(' ')}
      aria-hidden={!dirty}
    >
      {dirty ? signed(offset) : '+0.000'}
    </p>
  )

  if (!isActive || disabled) {
    return (
      <div className="ml-auto w-[5.25rem]">
        <p
          className={[
            'font-mono text-sm font-semibold tabular-nums text-slate-900',
            isActive && dirty ? 'text-blue-800' : '',
          ].join(' ')}
        >
          {fmtRating(value)}
        </p>
        {offsetLine}
      </div>
    )
  }

  return (
    <div className="ml-auto w-[5.5rem]">
      <input
        type="text"
        inputMode="decimal"
        aria-label="Effective rating"
        className={[
          'w-full rounded-md border px-2 py-1 text-right font-mono text-sm font-semibold tabular-nums shadow-sm',
          'border-slate-300 bg-white text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/25',
          dirty ? 'text-blue-800' : '',
        ].join(' ')}
        value={editing ? text : fmtRating(value)}
        onFocus={() => {
          onActivate()
          setEditing(true)
          setText(fmtRating(value))
        }}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commit()
            ;(e.currentTarget as HTMLInputElement).blur()
          } else if (e.key === 'Escape') {
            e.preventDefault()
            setEditing(false)
            setText(fmtRating(value))
            ;(e.currentTarget as HTMLInputElement).blur()
          }
        }}
      />
      {offsetLine}
    </div>
  )
}

function SliderWithHistory({
  value,
  history,
  baseline,
  disabled,
  onActivate,
  onChange,
}: {
  value: number
  history: PsRatingRow['history']
  baseline: number
  disabled: boolean
  onActivate: () => void
  onChange: (value: number) => void
}) {
  const marks = [baseline, ...history.map((h) => h.new_rating)].filter(Number.isFinite)

  return (
    <div className="py-1">
      <div className="relative h-8 w-full">
        <input
          type="range"
          tabIndex={-1}
          min={PS_RATING_MIN}
          max={PS_RATING_MAX}
          step={PS_RATING_STEP}
          value={value}
          disabled={disabled}
          onMouseDown={() => {
            if (!disabled) onActivate()
          }}
          onChange={(e) => onChange(Number(e.target.value))}
          className="range-plain absolute inset-x-0 top-1/2 w-full -translate-y-1/2 disabled:cursor-not-allowed disabled:opacity-50"
        />
        {marks.map((mark, i) => {
          const pct = ((mark - PS_RATING_MIN) / (PS_RATING_MAX - PS_RATING_MIN)) * 100
          const hist = history.find((h) => Math.abs(h.new_rating - mark) < 0.0005)
          return (
            <span
              key={`${mark}-${i}`}
              className="pointer-events-none absolute top-1/2 h-5 w-px -translate-y-1/2 rounded bg-slate-400"
              style={{ left: `${Math.max(0, Math.min(100, pct))}%` }}
              title={
                hist
                  ? `${fmtRating(mark)} on ${formatDateTime(hist.changed_at)}${hist.reason ? `: ${hist.reason}` : ''}`
                  : `Baseline ${fmtRating(mark)}`
              }
            />
          )
        })}
      </div>
    </div>
  )
}
